import { createContext, PropsWithChildren, useContext, useState, useMemo, useEffect } from "react";
import { defaultInactivityTimerValues, useInactivityTimer } from "./inactivityTimer";
import { resetLocalSavedUsername } from "./savedUsername";
import { redirectToCognitoLogout } from "~/utils/signIn";
import { retryFetch } from "@caredna-marlin/utils/client";
import type { TokenResponse } from '@caredna-marlin/utils/auth';
import { listRegisteredWsConnections, pruneDeadWsConnections } from "./wsConnectionRegistry";

const ACCESS_EXPIRES_LOCAL_STORAGE_KEY = "marlin-access-expires"
const USER_EMAIL_LOCAL_STORAGE_KEY = "marlin-current-user"
const IDLE_TIME_LOCAL_STORAGE_KEY = "marlin-idle-time";
const LOGIN_REDIRECT_PATH_LOCAL_STORAGE_KEY = "marlin-login-redirect-path";
const PRE_LOGIN_EMAIL_LOCAL_STORAGE_KEY = "marlin-pre-login-email";

export const TOKEN_REFRESH_BUFFER_MS = 120000; // Refresh 2 minutes before expiry.
// const TOKEN_REFRESH_BUFFER_MS = 1770000; // 29.5 minutes; cause frequent refreshes for testing (cognito)
// const TOKEN_REFRESH_BUFFER_MS = 3570000; // 59.5 minutes; cause frequent refreshes for 1hr auth tokens (fhir demo app)

type AuthContextType = {
  isAuthenticated: boolean | undefined;
  userEmail: string | undefined;
  accessExpires: Date | undefined;
  loggedIn: (json: TokenResponse) => string;
  logout: (forgetUsername?: boolean) => Promise<void>;
  inactivity: ReturnType<typeof useInactivityTimer>;
  isLoggingOut: boolean;
  setLoginRedirectPath: (path: string) => void;
};

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  userEmail: undefined,
  accessExpires: undefined,
  loggedIn: () => "/",
  logout: async () => { },
  inactivity: defaultInactivityTimerValues,
  isLoggingOut: false,
  setLoginRedirectPath: () => { },
});

type Props = PropsWithChildren;

function getAuthData(): { accessExpires: Date | undefined; userEmail: string | null } {
  const accessExpiresString = localStorage.getItem(ACCESS_EXPIRES_LOCAL_STORAGE_KEY);
  const accessExpiresNumber = parseInt(accessExpiresString || "")
  const accessExpires = accessExpiresNumber ? new Date(accessExpiresNumber) : undefined;
  const userEmail = localStorage.getItem(USER_EMAIL_LOCAL_STORAGE_KEY);

  return { accessExpires, userEmail };
}

function shouldRefreshAccessToken(accessExpires: Date | undefined): boolean {
  if (!accessExpires) return false;
  const now = new Date();
  const timeUntilExpiry = accessExpires.getTime() - now.getTime();
  return timeUntilExpiry <= TOKEN_REFRESH_BUFFER_MS;
}

export const AuthProvider = ({ children }: Props) => {
  const [accessExpires, setAccessExpires] = useState<Date | undefined>(undefined);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(undefined);
  const [userEmail, setUserEmail] = useState<string | undefined>(undefined);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const setAccessExpiresAndLocalStorage = (newValue: Date | undefined) => {
    if (newValue) {
      localStorage.setItem(ACCESS_EXPIRES_LOCAL_STORAGE_KEY, newValue.getTime().toString());
    } else {
      localStorage.removeItem(ACCESS_EXPIRES_LOCAL_STORAGE_KEY);
    }
    setAccessExpires(newValue);
  }

  const setUserEmailAndLocalStorage = (newUserEmail: string | undefined) => {
    if (newUserEmail) {
      localStorage.setItem(USER_EMAIL_LOCAL_STORAGE_KEY, newUserEmail)
    } else {
      localStorage.removeItem(USER_EMAIL_LOCAL_STORAGE_KEY);
    }
    setUserEmail(newUserEmail);
  }

  // Set initial authentication status from localStorage
  useEffect(() => {
    if (typeof isAuthenticated === 'undefined') {
      const data = getAuthData();
      if (data?.accessExpires) {
        setAccessExpires(data.accessExpires);
        if (data.accessExpires > new Date()) {
          const idleTime = getLocalIdleTime();
          if (!idleTime || idleTime > new Date()) {
            setUserEmail(data?.userEmail || undefined);
            setIsAuthenticated(true);
            return;
          }
        }
      }
      setUserEmail(undefined);
      setIsAuthenticated(false);
    }
  }, [isAuthenticated]);

  const loggedIn = (json: TokenResponse) => {
    setIsAuthenticated(json.authenticated);
    setUserEmailAndLocalStorage(json.email);
    setAccessExpiresAndLocalStorage(json.authenticated ? new Date(json.accessExpires) : undefined);


    if (json.authenticated) {
      // If the login process (eg, FHIR) provided us with a context, send the user directly to that patient.
      if (json.patientId) {
        return `/patients/${json.patientId}`;
      }

      // If we don't have a context, then send them to the URL they were trying to access, or the
      // root as a fallback.
      const lastPath = getLocalLoginRedirectPath() || "/";
      resetLocalLoginRedirectPath();
      return lastPath;
    }

    return "/login";
  }

  const logout = async (forgetUsername: boolean = true): Promise<void> => {
    setIsLoggingOut(true);

    if (forgetUsername) {
      resetLocalSavedUsername();
    }
    setIsAuthenticated(false);
    setUserEmailAndLocalStorage(undefined);
    setAccessExpiresAndLocalStorage(undefined);
    resetLocalIdleTime();

    try {
      await retryFetch(() => fetch(
        import.meta.env.VITE_SERVICE_HOST + "/token-revoke",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: 'include',
        }
      ));
    } catch (error) {
      console.error("Service revoke failed", error);
    }

    redirectToCognitoLogout();
    return;
  }

  // Timeout to refresh access token before it expires.
  useEffect(() => {
    if (accessExpires) {
      const timeUntilExpiry = accessExpires.getTime() - new Date().getTime();
      const timeUntilRefresh = timeUntilExpiry - TOKEN_REFRESH_BUFFER_MS;
      const refreshTimeoutId = setTimeout(refreshAccessToken, timeUntilRefresh);
      return () => clearTimeout(refreshTimeoutId);
    }
  }, [accessExpires]);

  const refreshAccessToken = async (): Promise<void> => {
    // Check the cookie to see if another tab refreshed already.
    const authData = getAuthData();
    if (authData?.accessExpires && !shouldRefreshAccessToken(authData.accessExpires)) {
      // Update access expires to trigger a new timeout
      setAccessExpiresAndLocalStorage(authData.accessExpires);
      return;
    }

    // This stops multiple tabs from refreshing at the same time. The tab that wins the lock acts
    // as the leader: it also extends the ttl of every websocket connection any tab on this
    // browser has registered (sockets are tab-scoped, but the refresh doesn't need to be).
    await navigator.locks.request("marlin.refresh.token", { ifAvailable: true }, async (lock) => {
      if (!lock) {
        // Try again in 10 seconds when localStorage should be updated with the new expiration time
        setAccessExpiresAndLocalStorage(new Date(Date.now() + TOKEN_REFRESH_BUFFER_MS + 10000));
        return;
      }

      const wsConnectionIds = listRegisteredWsConnections();
      const response = await retryFetch(() => fetch(import.meta.env.VITE_SERVICE_HOST + "/token-refresh", {
        method: "POST",
        credentials: 'include',
        ...(wsConnectionIds.length > 0 ? {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ wsConnectionIds }),
        } : {}),
      }));
      const json = await response.json();
      setIsAuthenticated(json.authenticated);
      setUserEmailAndLocalStorage(json.email)
      setAccessExpiresAndLocalStorage(json.authenticated ? new Date(json.accessExpires) : undefined);
      if (json.deadWsConnectionIds) {
        pruneDeadWsConnections(json.deadWsConnectionIds);
      }
    });
  };

  const inactivity = useInactivityTimer({
    disabled: !isAuthenticated,
    onInactive: () => logout(false),
    onAction: (idleAt) => {
      const existingLocalTime = getLocalIdleTime();
      if (!existingLocalTime || idleAt > existingLocalTime) {
        setLocalIdleTime(idleAt);
      }
    },
  });

  const setLoginRedirectPath = (path: string) => {
    setLocalLoginRedirectPath(path);
  }

  const value = useMemo(
    () => ({
      isAuthenticated,
      userEmail,
      accessExpires,
      loggedIn,
      logout,
      inactivity,
      isLoggingOut,
      setLoginRedirectPath,
    }),
    [isAuthenticated, inactivity, isLoggingOut, userEmail]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

const getLocalIdleTime = (): Date | undefined => {
  const timeString = localStorage.getItem(IDLE_TIME_LOCAL_STORAGE_KEY);
  if (timeString) {
    return new Date(parseInt(timeString));
  }
}

const setLocalIdleTime = (idleTime: Date) => {
  localStorage.setItem(
    IDLE_TIME_LOCAL_STORAGE_KEY,
    idleTime.getTime().toString()
  );
}

const resetLocalIdleTime = () => {
  localStorage.removeItem(IDLE_TIME_LOCAL_STORAGE_KEY);
}

const getLocalLoginRedirectPath = () => {
  return localStorage.getItem(LOGIN_REDIRECT_PATH_LOCAL_STORAGE_KEY);
}

const setLocalLoginRedirectPath = (redirectPath: string) => {
  localStorage.setItem(LOGIN_REDIRECT_PATH_LOCAL_STORAGE_KEY, redirectPath);
}

const resetLocalLoginRedirectPath = () => {
  localStorage.removeItem(LOGIN_REDIRECT_PATH_LOCAL_STORAGE_KEY);
}

export const getLocalPreLoginEmail = () => {
  return localStorage.getItem(PRE_LOGIN_EMAIL_LOCAL_STORAGE_KEY);
}

export const setLocalPreLoginEmail = (email: string) => {
  localStorage.setItem(PRE_LOGIN_EMAIL_LOCAL_STORAGE_KEY, email);
}

export const resetLocalPreLoginEmail = () => {
  localStorage.removeItem(PRE_LOGIN_EMAIL_LOCAL_STORAGE_KEY);
}
